/**
 * 行ベースのナレーション (voice-lines・歌モノ以外のすべてのシナリオ) の画面用の純関数
 * (docs/features/brand-cut-motion-graphics-plan.md §10.7.3 / §10.7.4 / §10.7.5)。
 *
 * - 声にする行 (TTS の行) の振り分け (motion-v2 は dialogue_lines 全行・実写は narration 欄 + 声だけの登場人物の行)
 * - 声の生成 / 1 行の撮り直しの受付エラーの分類 (未割り当ての話者・混在・409 の撮り直し拒否)
 * - ジョブの結果の要約 (失敗したシーン・計画の advice)
 * - 声の鮮度 (sync-status の voice) の注意文と、「AI で修正」の無効化の理由
 * - 料金の見積り (シーン × そのシーンで使うプロバイダごとに 1 回・ナレーションのレート)
 * - 実行中の声のジョブの控え (再読み込みで追跡を戻す・タブ単位)
 *
 * 正本の判定は backend。ここは画面の出し分けだけを持つ。
 */
import type {
  MotionV2ConvertDryRun,
  VoiceLineTiming,
  VoiceLinesJobResult,
  VoiceSpeaker,
} from '../../../features/scenarios/api';
import type { DialogueLine, MotionSyncStatus, MotionVoiceStatus, TelopLine } from '../../../features/scenarios/types';
import { NARRATION_RESOURCE_TYPE_BY_PROVIDER, type NarrationProviderCode } from './constants';
import { resolveNarrationText } from './narrationUtils';
import {
  MOTION_V2_DIALOGUE_LINES_MAX_COUNT,
  SCRIPT_CONTRACT_MOTION_V2,
  narrationVoiceSpeaker,
} from '../../../features/scenarios/scriptContract';

// ── 受付エラーの分類 ──

export const VOICE_LINES_CHANGED_MESSAGE = '他の行も変わっています。このシーンのナレーションを生成し直してください';
export const VOICE_SOURCE_MIXED_MESSAGE =
  'このシーンは動画 AI が発話する行と TTS の行が混在しています。声だけの登場人物の行を別のシーンに分けるか、読み手を揃えてください';
export const VOICE_LINES_EMPTY_MESSAGE = 'ナレーションにする行がありません。シーンにナレーションの行を書いてから生成してください';
export const VOICE_MATERIAL_MISSING_MESSAGE = 'このシーンのナレーションがまだありません。先に「このナレーションだけ生成」を実行してください';
export const VOICE_LINE_NOT_FOUND_MESSAGE = 'この行が見つかりません。台本が変わった可能性があるので、画面を再読み込みしてからやり直してください';
export const VOICE_SPEAKER_UNASSIGNED_MESSAGE = '声が割り当てられていない話者がいます。話者に声を割り当ててから生成してください';

type OrderRef = { scene_id?: string; sceneId?: string; order?: number | null };

function orderList(scenes: readonly OrderRef[] | null | undefined): string {
  return [...(scenes || [])]
    .map((s) => s.order)
    .filter((o): o is number => typeof o === 'number')
    .sort((a, b) => a - b)
    .map((o) => `シーン ${o}`)
    .join('・');
}

export type VoiceLinesErrorAction =
  | { kind: 'purchase'; message: string }
  | { kind: 'unassigned'; speakers: string[]; message: string }
  | { kind: 'mixed'; scenes: OrderRef[]; message: string }
  | { kind: 'lines_changed'; changedLineIds: string[]; message: string }
  | { kind: 'running'; jobId: string | null; message: string }
  | { kind: 'message'; message: string };

export interface VoiceLinesErrorBody {
  error?: string;
  error_code?: string;
  speakers?: string[];
  scenes?: OrderRef[];
  changed_line_ids?: string[];
  reason?: string;
  job_id?: string;
}

/** 声の生成 / 撮り直しの受付エラー (HTTP 状態 + 応答) を画面の扱いに振り分ける。 */
export function classifyVoiceLinesError(
  status: number | null | undefined,
  data: VoiceLinesErrorBody | null | undefined,
  fallback: string,
): VoiceLinesErrorAction {
  const d = data || {};
  if (status === 402 || d.error_code === 'INSUFFICIENT_CREDITS') {
    return { kind: 'purchase', message: d.error || 'ポイントが不足しています' };
  }
  if (d.error_code === 'VOICE_SPEAKER_UNASSIGNED') {
    return { kind: 'unassigned', speakers: Array.isArray(d.speakers) ? d.speakers : [], message: VOICE_SPEAKER_UNASSIGNED_MESSAGE };
  }
  if (d.error_code === 'VOICE_SOURCE_MIXED') {
    const where = orderList(d.scenes);
    return { kind: 'mixed', scenes: d.scenes || [], message: where ? `${where}: ${VOICE_SOURCE_MIXED_MESSAGE}` : VOICE_SOURCE_MIXED_MESSAGE };
  }
  if (d.error_code === 'VOICE_LINES_EMPTY') {
    const where = orderList(d.scenes);
    return { kind: 'message', message: where ? `${where}: ${VOICE_LINES_EMPTY_MESSAGE}` : VOICE_LINES_EMPTY_MESSAGE };
  }
  if (d.error_code === 'VOICE_MATERIAL_MISSING') {
    return { kind: 'message', message: VOICE_MATERIAL_MISSING_MESSAGE };
  }
  if (d.error_code === 'VOICE_LINE_NOT_FOUND') {
    return { kind: 'message', message: VOICE_LINE_NOT_FOUND_MESSAGE };
  }
  if (d.error_code === 'VOICE_JOB_ACTIVE') {
    return { kind: 'running', jobId: d.job_id ?? null, message: d.error || 'ナレーションを生成中です' };
  }
  if (d.error_code === 'VOICE_LINES_CHANGED') {
    return {
      kind: 'lines_changed',
      changedLineIds: Array.isArray(d.changed_line_ids) ? d.changed_line_ids : [],
      message: VOICE_LINES_CHANGED_MESSAGE,
    };
  }
  if (d.error_code === 'NARRATION_USE_VOICE_LINES') {
    return { kind: 'message', message: d.error || 'このシナリオは話者付きの台本です。ナレーションのセクションから生成してください' };
  }
  if (status === 409) {
    return { kind: 'running', jobId: d.job_id ?? null, message: d.error || 'ナレーションを生成中です' };
  }
  return { kind: 'message', message: d.error || fallback };
}

// ── ジョブの結果 ──

function adviceText(plan: VoiceLinesJobResult['plan'] | null | undefined): string | null {
  if (!plan) return null;
  const advice = plan.advice;
  if (typeof advice === 'string' && advice.trim()) return advice.trim();
  if (advice && typeof advice === 'object' && advice.message?.trim()) return advice.message.trim();
  if (plan.advice_detail?.message?.trim()) return plan.advice_detail.message.trim();
  const reasons = (plan.reasons || []).filter((r) => typeof r === 'string' && r.trim());
  return reasons.length > 0 ? reasons.join('・') : null;
}

/** 声のジョブの結果 → トーストの文言・失敗したシーンの一覧・計画の注意。 */
export function summarizeVoiceLinesResult(
  result: Partial<VoiceLinesJobResult> | null | undefined,
  kind: 'generate' | 'retake',
): { message: string; failedLines: string[]; advice: string | null; sceneIds: string[]; allFailed: boolean } {
  const scenes = Array.isArray(result?.scenes) ? result!.scenes : [];
  const failed = Array.isArray(result?.failed) ? result!.failed : [];
  const failedLines = [...failed]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((f) => `シーン ${f.order ?? '?'}: ${f.error}`);
  const advice = result?.plan?.status === 'advice' ? (adviceText(result.plan) ?? 'ナレーションが長く、シーンの尺に収まりません。台本を短くしてください') : null;
  // ジョブは全シーンが失敗しても completed で終わる (backend)。成功 0 件で失敗があれば失敗として扱う
  const allFailed = scenes.length === 0 && failed.length > 0;
  let message: string;
  if (kind === 'retake') {
    message = allFailed
      ? '行の撮り直しに失敗しました'
      : scenes.length > 0 ? `シーン ${scenes[0]!.order ?? '?'} の行を撮り直しました` : '行の撮り直しが完了しました';
  } else if (scenes.length > 0 && failed.length > 0) {
    message = `${scenes.length} シーンのナレーションを生成しました (${failed.length} シーンは失敗)`;
  } else if (scenes.length > 0) {
    message = `${scenes.length} シーンのナレーションを生成しました`;
  } else if (failed.length > 0) {
    message = 'ナレーションの生成に失敗しました';
  } else {
    message = 'ナレーションの生成が完了しました';
  }
  return { message, failedLines, advice, sceneIds: scenes.map((s) => s.scene_id), allFailed };
}

// ── 鮮度 ──

/**
 * シーンのナレーションの鮮度。motion-v2 は sync-status の voice (videoStale / reason を含む)、
 * それ以外は voice-map の scene_status (needsRegenerate / needsRegenerateReason だけ)。
 */
export type SceneVoiceStatus = {
  needsRegenerate: boolean | null;
  needsRegenerateReason?: string | null;
  videoStale?: boolean;
  reason?: string | null;
};

/** シーンの「ナレーションの再生成が必要」の文言 (無ければ undefined)。 */
export function voiceRegenerateNotice(voice: SceneVoiceStatus | null | undefined): string | undefined {
  if (!voice?.needsRegenerate) return undefined;
  // 理由は backend の「未生成」「声の素材が差し替えられた」「台本か声の設定が変わった」のいずれか
  if (voice.needsRegenerateReason === '未生成') return 'ナレーションが未生成です';
  return voice.needsRegenerateReason ? `ナレーションの再生成が必要: ${voice.needsRegenerateReason}` : 'ナレーションの再生成が必要';
}

/** シーンの「映像の再生成が必要」の文言 (無ければ undefined)。 */
export function voiceVideoStaleNotice(voice: SceneVoiceStatus | null | undefined): string | undefined {
  if (!voice?.videoStale) return undefined;
  return `映像の再生成が必要 (${voice.reason || 'ナレーションが変わった'})`;
}

/**
 * 「AI で修正」(全編) を無効にする理由。声が変わったシーン (videoStale) が 1 つでもあれば、backend も
 * 400 (MOTION_VOICE_STALE) で断るので押させない。対象が無ければ null。
 */
export function motionReviseVoiceBlockReason(
  statuses: Readonly<Record<string, MotionSyncStatus>> | null | undefined,
  scenes: ReadonlyArray<{ id: string; order: number }> | null | undefined,
): string | null {
  const stale = (scenes || [])
    .filter((s) => statuses?.[s.id]?.voice?.videoStale)
    .map((s) => s.order)
    .sort((a, b) => a - b);
  if (stale.length === 0) return null;
  return `ナレーションが変わったシーンがあるため AI で修正できません (${stale.map((o) => `シーン ${o}`).join('・')})。全編を生成し直してください`;
}

/** 400 MOTION_VOICE_STALE の応答 → 画面の文言 (シーン番号と理由を添える)。該当しなければ null。 */
export function motionVoiceStaleMessage(
  data: { error?: string; error_code?: string; scenes?: Array<{ order?: number | null; reason?: string | null }> } | null | undefined,
): string | null {
  if (data?.error_code !== 'MOTION_VOICE_STALE') return null;
  const head = data.error || 'ナレーションが変わっています。全編を生成し直してください';
  const detail = (data.scenes || [])
    .filter((s) => typeof s.order === 'number')
    .map((s) => `シーン ${s.order}: ${s.reason || 'ナレーションが変わった'}`)
    .join('・');
  return detail ? `映像の再生成が必要 (ナレーションが変わった)。${head} (${detail})` : `映像の再生成が必要 (ナレーションが変わった)。${head}`;
}

// ── 声にする行 (TTS の行) の振り分け ──

/**
 * 実写の narration 欄をナレーター行にしたときの line_id
 * (backend services/scenario/generation/voice_lines.py NARRATION_FIELD_LINE_ID と同値。dialogue_lines の 8 桁 16 進とは衝突しない)。
 */
export const NARRATION_FIELD_LINE_ID = 'narration';

type VoiceScenarioMetaSource = { generation_metadata?: Record<string, unknown> | null };

type VoiceSceneSource = {
  id: string;
  order: number;
  brand_cut_position?: string | null;
  audio_track_type?: 'narration' | 'sound_logo' | null;
  narration?: string | null;
  /** 旧形式 (narration / dialogue 分離前) の原稿。resolveNarrationText のフォールバックに使う */
  dialogue?: string | null;
  script?: string | null;
  dialogue_lines?: DialogueLine[] | null;
  narration_material_id?: string | null;
  narration_excluded?: boolean | null;
};

type VoiceScenarioSource<S extends VoiceSceneSource = VoiceSceneSource> = VoiceScenarioMetaSource & {
  scenes?: readonly S[] | null;
};

function voiceMetaOf(scenario: VoiceScenarioMetaSource | null | undefined): Record<string, unknown> {
  const meta = scenario?.generation_metadata;
  return meta && typeof meta === 'object' ? meta : {};
}

/**
 * ナレーションを行ベース (voice-lines) で扱うシナリオか。歌モノ (bgm_mode='lyrics') 以外のすべて。
 * 歌モノは narration が歌詞の正本で Lyria が歌うため TTS の UI を出さない。
 */
export function usesVoiceLines(scenario: VoiceScenarioMetaSource | null | undefined): boolean {
  return voiceMetaOf(scenario).bgm_mode !== 'lyrics';
}

/** 声だけの登場人物 (generation_metadata.characters[].appears_on_screen === false) の character_key。 */
export function offscreenCharacterKeys(scenario: VoiceScenarioMetaSource | null | undefined): Set<string> {
  const characters = voiceMetaOf(scenario).characters;
  if (!Array.isArray(characters)) return new Set();
  return new Set(
    characters
      .filter((ch): ch is { key: string; appears_on_screen?: boolean } => !!ch && typeof ch === 'object'
        && typeof (ch as { key?: unknown }).key === 'string'
        && (ch as { appears_on_screen?: unknown }).appears_on_screen === false)
      .map((ch) => ch.key),
  );
}

/**
 * dialogue_lines の 1 行を TTS で読むか (backend voice_source.dialogue_line_voice_source と同じ規則)。
 * motion-v2 は全行 TTS。実写は voice_source の明示に従い、無指定なら声だけの登場人物の行だけ TTS (他は動画 AI の発話)。
 */
export function isTtsDialogueLine(
  line: Pick<DialogueLine, 'speaker' | 'voice_source'>,
  opts: { motionV2: boolean; offscreenKeys: ReadonlySet<string> },
): boolean {
  if (opts.motionV2) return true;
  if (line.voice_source === 'tts') return true;
  if (line.voice_source === 'video_ai') return false;
  const key = line.speaker?.character_key;
  return !!key && opts.offscreenKeys.has(key);
}

/**
 * dialogue_lines 全行の TTS 判定 (行と同じ並び・backend voice_source.scene_dialogue_voice_sources と同じ規則)。
 * 実写で、声だけの登場人物の行が「既定で TTS」になるのは同じシーンに動画 AI が発話する行が無いときだけ
 * (画面内の人物との会話では従来どおり動画 AI に渡す)。voice_source='tts' の明示はそのまま。
 */
export function sceneDialogueTtsFlags(
  lines: readonly Pick<DialogueLine, 'speaker' | 'voice_source'>[],
  opts: { motionV2: boolean; offscreenKeys: ReadonlySet<string> },
): boolean[] {
  const flags = lines.map((l) => isTtsDialogueLine(l, opts));
  if (opts.motionV2 || !flags.some((f) => !f)) return flags;
  return lines.map((l, i) => (l.voice_source === 'tts' || l.voice_source === 'video_ai' ? flags[i] : false));
}

/**
 * シーンの TTS の行 (ナレーションとして読み上げて連結する行・台本の順)。
 * - motion-v2: dialogue_lines の全行
 * - 実写: narration 欄 (非空) をナレーター行 (line_id = NARRATION_FIELD_LINE_ID) として先頭に置き、
 *   続けて TTS の dialogue 行 (isTtsDialogueLine)
 * - 除外行・空文字の行は除く。ブランドカット・サウンドロゴのシーン・歌モノは []
 */
export function sceneTtsLines(scenario: VoiceScenarioMetaSource | null | undefined, scene: VoiceSceneSource): DialogueLine[] {
  if (!usesVoiceLines(scenario) || scene.brand_cut_position || scene.audio_track_type === 'sound_logo') return [];
  const motionV2 = voiceMetaOf(scenario).script_contract === SCRIPT_CONTRACT_MOTION_V2;
  const offscreenKeys = motionV2 ? new Set<string>() : offscreenCharacterKeys(scenario);
  const rawLines = (scene.dialogue_lines || []).filter((l): l is DialogueLine => !!l);
  const flags = sceneDialogueTtsFlags(rawLines, { motionV2, offscreenKeys });
  const dialogue = rawLines.filter((l, i) => flags[i] && !l.excluded && !!l.text?.trim());
  if (motionV2) return dialogue;
  // 旧形式 (narration が空で script に原稿) も backend (resolve_narration_text) と同じく script に落とす
  const narration = resolveNarrationText({ narration: scene.narration ?? undefined, dialogue: scene.dialogue ?? undefined, script: scene.script ?? undefined }).trim();
  const narrationLine: DialogueLine[] = narration
    ? [{ line_id: NARRATION_FIELD_LINE_ID, speaker: narrationVoiceSpeaker(), text: narration, voice_source: 'tts' }]
    : [];
  return [...narrationLine, ...dialogue];
}

/** 声の対象シーン (ブランドカット・サウンドロゴ以外で TTS の行が 1 つ以上あるシーン)。 */
export function voiceTargetScenes<S extends VoiceSceneSource>(scenario: VoiceScenarioSource<S> | null | undefined): S[] {
  // ナレーションを採用しないシーンは backend の全シーン生成 (scene_ids 省略) でも対象外 (見積もり・事前検証・件数を揃える)
  return (scenario?.scenes || []).filter((s) => !s.narration_excluded && sceneTtsLines(scenario, s).length > 0);
}

/**
 * 声が未生成 / 古いシーン (全編を生成する前の注意・生成済み本数の表示用)。声の素材が無い・除外・「声の再生成が必要」のシーンを order 順で返す。
 * statuses はシーン id → 鮮度 (motion は sync-status の voice、それ以外は voice-map の scene_status)。
 */
export function scenesNeedingVoice<S extends VoiceSceneSource>(
  scenario: VoiceScenarioSource<S> | null | undefined,
  statuses: Readonly<Record<string, SceneVoiceStatus | null | undefined>> | null | undefined,
): S[] {
  return voiceTargetScenes(scenario)
    .filter((s) => !s.narration_material_id || !!s.narration_excluded || statuses?.[s.id]?.needsRegenerate === true)
    .sort((a, b) => a.order - b.order);
}

/** motion の sync-status (シーン id → MotionSyncStatus) から声の鮮度だけを取り出す。 */
export function voiceStatusesFromMotionSync(
  statuses: Readonly<Record<string, MotionSyncStatus>> | null | undefined,
): Record<string, MotionVoiceStatus> {
  const out: Record<string, MotionVoiceStatus> = {};
  for (const [sceneId, st] of Object.entries(statuses || {})) {
    if (st?.voice) out[sceneId] = st.voice;
  }
  return out;
}

/**
 * シーンごとの英語発音の指定 → POST voice-lines/generate の scene_languages。
 * englishAll (三点メニューの「英語で発音する (全シーン)」) なら対象の全シーンを 'en'。指定の無いシーンは含めない (backend の既定 ja)。
 */
export function buildSceneLanguages(
  scenes: ReadonlyArray<{ id: string; key: string }>,
  englishScenes: Readonly<Record<string, boolean>> | null | undefined,
  englishAll = false,
): Record<string, 'ja' | 'en'> | undefined {
  const out: Record<string, 'ja' | 'en'> = {};
  for (const { id, key } of scenes) {
    if (englishAll || englishScenes?.[key]) out[id] = 'en';
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export const MOTION_VOICE_FIRST_NOTICE = 'ナレーションを先に作ると動きをナレーションに合わせられます';

// ── 料金の見積り ──

type CreditRateRow = { service_type: string; resource_type: string; credits_per_unit: number };

/**
 * 声の生成の見積り (シーン × そのシーンで使うプロバイダごとに 1 回・ナレーションのレート)。
 * 声の割り当てが無い話者は数えない (受付で 400 になる)。レートが無いプロバイダは null を返す。
 */
export function estimateVoiceLinesCredits(
  speakers: readonly VoiceSpeaker[] | null | undefined,
  sceneIds: readonly string[],
  rates: readonly CreditRateRow[] | null | undefined,
): number | null {
  const targets = new Set(sceneIds);
  const pairs = new Set<string>();
  for (const sp of speakers || []) {
    if (!sp.voice?.provider) continue;
    for (const sceneId of sp.used_in_scene_ids || []) {
      if (targets.has(sceneId)) pairs.add(`${sceneId}\u0000${sp.voice.provider}`);
    }
  }
  let total = 0;
  for (const pair of pairs) {
    const provider = pair.split('\u0000')[1] as NarrationProviderCode;
    const resourceType = NARRATION_RESOURCE_TYPE_BY_PROVIDER[provider];
    const rate = rates?.find((r) => r.service_type === 'narration' && r.resource_type === resourceType);
    if (!rate) return null;
    total += Math.max(1, Math.ceil(Number(rate.credits_per_unit)));
  }
  return total;
}

/** 今回生成する行の話者で、声の割り当てが無いもの (speaker_id)。 */
export function unassignedSpeakerIds(
  speakers: readonly VoiceSpeaker[] | null | undefined,
  sceneIds?: readonly string[] | null,
): string[] {
  const targets = sceneIds ? new Set(sceneIds) : null;
  return (speakers || [])
    .filter((sp) => !sp.voice && (!targets || (sp.used_in_scene_ids || []).some((id) => targets.has(id))))
    .map((sp) => sp.speaker_id);
}

// ── 声の素材の行の時刻 ──

/** 声の素材 (generation_metadata.voice_lines) の行の時刻。無ければ []。 */
export function readVoiceLineTimings(material: { generation_metadata?: Record<string, unknown> | null } | null | undefined): VoiceLineTiming[] {
  const raw = material?.generation_metadata?.voice_lines;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (l): l is VoiceLineTiming => !!l && typeof l === 'object'
      && typeof (l as VoiceLineTiming).line_id === 'string'
      && Number.isFinite((l as VoiceLineTiming).startSec)
      && Number.isFinite((l as VoiceLineTiming).endSec),
  );
}

export function formatVoiceSec(sec: number): string {
  return `${(Math.round(sec * 10) / 10).toFixed(1)} 秒`;
}

// ── 実行中の声のジョブの控え (再読み込みで追跡を戻す・タブ単位) ──

const VOICE_JOB_STORAGE_PREFIX = 'voice_lines_job:';

export interface RememberedVoiceJob {
  jobId: string;
  kind: 'generate' | 'retake';
  /** 生成中のシーン (全シーンなら空) */
  sceneIds: string[];
}

export function rememberVoiceJob(scenarioId: string, job: RememberedVoiceJob): void {
  try { sessionStorage.setItem(`${VOICE_JOB_STORAGE_PREFIX}${scenarioId}`, JSON.stringify(job)); } catch { /* ignore */ }
}

export function recallVoiceJob(scenarioId: string): RememberedVoiceJob | null {
  try {
    const raw = sessionStorage.getItem(`${VOICE_JOB_STORAGE_PREFIX}${scenarioId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedVoiceJob>;
    if (!parsed || typeof parsed.jobId !== 'string') return null;
    return {
      jobId: parsed.jobId,
      kind: parsed.kind === 'retake' ? 'retake' : 'generate',
      sceneIds: Array.isArray(parsed.sceneIds) ? parsed.sceneIds.filter((x): x is string => typeof x === 'string') : [],
    };
  } catch {
    return null;
  }
}

export function forgetVoiceJob(scenarioId: string, jobId?: string): void {
  try {
    if (jobId) {
      const current = recallVoiceJob(scenarioId);
      if (current && current.jobId !== jobId) return;
    }
    sessionStorage.removeItem(`${VOICE_JOB_STORAGE_PREFIX}${scenarioId}`);
  } catch { /* ignore */ }
}

// ── 再取得したシーンの取り込み (声の生成で変わる列だけ) ──

type RefreshedSceneRow = {
  id: string;
  dialogue_lines?: DialogueLine[] | null;
  telop_lines?: TelopLine[] | null;
  narration?: string | null;
  narration_material_id?: string | null;
  narration_voice_id?: string | null;
  narration_duration_seconds?: number | null;
  narration_excluded?: boolean | null;
  video_duration_seconds?: number | null;
  generation_metadata?: Record<string, unknown> | null;
};

/**
 * 声のジョブの完了後に取り直したシナリオから、声で変わる列 (声の素材・尺・台本・メタデータ) だけを画面のシーンへ写す。
 * 他の画面状態 (include_product 等) は触らない。取り直した側に無いシーンはそのまま残す。
 */
export function mergeVoiceRefreshedScenes<S extends { id: string }>(
  scenes: readonly S[],
  refreshed: readonly RefreshedSceneRow[],
): S[] {
  const byId = new Map(refreshed.map((r) => [r.id, r]));
  return scenes.map((scene) => {
    const r = byId.get(scene.id);
    if (!r) return scene;
    return {
      ...scene,
      dialogue_lines: r.dialogue_lines ?? null,
      telop_lines: r.telop_lines ?? null,
      narration: r.narration ?? '',
      narration_material_id: r.narration_material_id ?? undefined,
      narration_voice_id: r.narration_voice_id ?? undefined,
      narration_duration_seconds: r.narration_duration_seconds ?? undefined,
      narration_excluded: r.narration_excluded ?? undefined,
      video_duration_seconds: r.video_duration_seconds ?? undefined,
      generation_metadata: r.generation_metadata ?? undefined,
    } as S;
  });
}

/** 保存した声の対応表を画面のシナリオ (generation_metadata.voice_map) に写す (同期の鮮度の取り直しのきっかけになる)。 */
export function withScenarioVoiceMap<R extends { scenarios: Array<{ id: string; generation_metadata?: Record<string, unknown> }> }>(
  prev: R | null,
  scenarioId: string,
  voiceMap: Record<string, unknown>,
): R | null {
  if (!prev) return prev;
  return {
    ...prev,
    scenarios: prev.scenarios.map((s) => (s.id !== scenarioId ? s : {
      ...s,
      generation_metadata: { ...(s.generation_metadata ?? {}), voice_map: voiceMap },
    })),
  };
}

// ── 旧台本の変換 (§10.7.5) ──

export type MotionV2ConvertErrorBody = {
  error?: string;
  error_code?: string;
  scenes?: Array<{ order?: number | null; lines?: number; line?: number; chars?: number; error?: string }>;
};

/** 変換の本実行の 422 / 409 → 画面の文言 (シーン番号と行数付き)。 */
export function motionV2ConvertErrorMessage(status: number | undefined, data: MotionV2ConvertErrorBody | undefined): string {
  const d = data || {};
  if (status === 422 && d.error_code === 'VOICE_LINES_OVER_LIMIT' && d.scenes?.length) {
    const detail = d.scenes
      .map((s) => `シーン ${s.order ?? '?'} のナレーションが ${s.lines ?? '?'} 行になり上限 ${MOTION_V2_DIALOGUE_LINES_MAX_COUNT} 行を超えます`)
      .join('・');
    return `${detail}。ナレーションかセリフを短くしてから変換してください (何も変更していません)`;
  }
  if (status === 422 && d.error_code === 'VOICE_LINE_TOO_LONG' && d.scenes?.length) {
    const detail = d.scenes.map((s) => `シーン ${s.order ?? '?'} のセリフ ${s.line ?? '?'} 行目が ${s.chars ?? '?'} 字です`).join('・');
    return `${detail}。ナレーションの行は 60 字以内にしてから変換してください (何も変更していません)`;
  }
  if (status === 422) return `${d.error || '変換できないシーンがあります'} (何も変更していません)`;
  return d.error || '台本の変換に失敗しました';
}

/** dry_run の結果 → 変換できない理由の一覧 (空なら変換できる)。 */
export function motionV2ConvertBlockers(dry: MotionV2ConvertDryRun): string[] {
  return [
    ...dry.over_limit.map((s) => `シーン ${s.order ?? '?'}: ナレーションが ${s.lines} 行になり上限 ${MOTION_V2_DIALOGUE_LINES_MAX_COUNT} 行を超えます`),
    ...dry.too_long.map((s) => `シーン ${s.order ?? '?'}: セリフ ${s.line} 行目が ${s.chars} 字です (上限 60 字)`),
    ...dry.invalid.map((s) => `シーン ${s.order ?? '?'}: ${s.error}`),
  ];
}


// ── 画面設計 (visual) の定型 (§10.7.2) ──

/** 声の台本 (motion-v2) の画面設計 (visual) の定型。自由文のまま・パースはしない */
export const MOTION_V2_VISUAL_PLACEHOLDER = [
  '画面: (レイアウト・出す文字 / 数字 / 図形 / アイコン・商品画像や立ち絵の位置)',
  '動き: (登場・切り替え・強調の順序と、小節頭に置く切り替え)',
  '強調: (数字・キーワードのどれを大きく / 色で立てるか)',
].join('\n');
/** 画面設計の字数の目安 */
export const MOTION_V2_VISUAL_GUIDE = { min: 200, max: 400 };
